import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../utils/api";
import { produce } from "immer";
import { DateTime } from "luxon";

const normalizeIds = items => items.map(item => item.lead.id);

const addLead = ({ data }) => api.post(`/leads/`, { ...data }).then(res => res.data);
const updateLead = ({ id, data }) => api.put(`/leads/${id}/`, { ...data }).then(res => res.data);
const undoDeleteLead = ({ lead }) => api.put(`/leads/${lead.id}/restore/`).then(res => res.data);
const deleteLead = id => api.delete(`/leads/${id}/`).then(res => res.data);

// Bulk operations
const bulkEdit = data => api.put('/leads/bulk/', data).then(res => res.data);
const bulkDelete = ids => api.delete('/leads/', { data: { ids }}).then(res => res.data);
const bulkUndoDelete = items => api.put('/leads/restore/', { ids: normalizeIds(items) }).then(res => res.data);

const importLeads = ({ data }) => api.post(`/leads/import/`, data).then(res => res.data);

export default function useMutateLead({ categoryId }) {
  const queryClient = useQueryClient();

  const findLead = (id, draft) => {
    for (let page of draft.pages) {
      const index = page.data.findIndex(lead => lead.id === id);
      
      if (index !== -1) {
        const lead = page.data[index];
        return { page, lead, index }
      }
    }
  }

  const onMutateLead = variables => {
    const last_updated = DateTime.now().toUTC().toISO();

    queryClient.setQueriesData(['leads', categoryId], data => produce(data, draft => {
      const lead = findLead(variables.id, draft);
      
      if (lead) {
        lead.page.data[lead.index] = { ...lead.lead, ...variables.data, last_updated };
      }
    }));
  }

  const onUpdateSuccess = (requestData, { id, data }) => {
    const changedStatusId = data?.status_id;

    // if the status_id changed, find it inside the previous status page, delete, and add on top of new page:
    if (changedStatusId) {
      queryClient.setQueriesData(['leads-status'], data => produce(data, draft => {
        const lead = findLead(id, draft);

        if (lead) {
          lead.page.data.splice(lead.index, 1);
        }
      }));

      queryClient.setQueriesData(['leads-status', requestData.status_id], data => produce(data, draft => {
        draft.pages[0].data.unshift(requestData);
      }));
    }

    // status unchanged, find the lead and update the values with request data
    else {
      queryClient.setQueriesData(['leads-status', requestData.status_id], data => produce(data, draft => {
        const lead = findLead(id, draft);

        if (lead) {
          lead.page.data[lead.index] = requestData;
        }
      }));
    }

    queryClient.setQueriesData(['leads'], data => produce(data, draft => {
      const lead = findLead(id, draft);

      if (lead) {
        lead.page.data[lead.index] = requestData;
      }
    }));
  }

  const onAddLead = requestData => {
    queryClient.setQueriesData(['leads', categoryId], data => produce(data, draft => {
      // add the lead to the first page in the pagination:
      // TODO -> check why this gets errors
      console.log('data:', data)
      if (draft) {
        draft.pages[0].data.unshift(requestData);
      }
    }));

    queryClient.setQueryData(['leads-status', requestData.status_id], data => produce(data, draft => {
      console.log('data:', data)
      if (draft) {
        draft.pages[0].data.unshift(requestData);
      }
    }));
  }

  const onBulkDelete = ids => {
    queryClient.setQueriesData(['leads', categoryId], data => produce(data, draft => {
      draft.pages.forEach(page => {
        page.data = page.data.filter(lead => !ids.includes(lead.id))
      });
    }))
  }

  const onDelete = id => {
    queryClient.setQueriesData(['leads', categoryId], data => produce(data, draft => {
      const lead = findLead(id, draft);

      if (lead) {
        lead.page.data.splice(lead.index, 1);
      }
    }));

    queryClient.setQueriesData(['leads-status'], data => produce(data, draft => {
      const lead = findLead(id, draft);

      if (lead) {
        lead.page.data.splice(lead.index, 1);
      }
    }));

    queryClient.setQueriesData(['leads-kanban'], data => produce(data, draft => {
      const index = data.findIndex(lead => lead.id === id);

      if (index !== -1) {
        draft.splice(index, 1);
      }
    }));
  }

  const onBulkEdit = requestData => {
    queryClient.setQueriesData(['leads', categoryId], data => produce(data, draft => {
      requestData?.forEach(lead => {
        const item = findLead(lead.id, draft);

        if (item) {
          item.page.data[item.index] = lead;
        }
      });
    }));
  }

  const onUndoDelete = ({ lead, pageIndex, index }) => {
    queryClient.setQueriesData(['leads', categoryId], data => produce(data, draft => {
      draft.pages[pageIndex].data.splice(index, 0, lead);
    }));
  }

  const onBulkUndoDelete = items => {
    items.forEach(item => {
      queryClient.setQueriesData(['leads', categoryId], data => produce(data, draft => {
        draft.pages[item.pageIndex].data.splice(item.index, 0, item.lead);
      }))
    });
  }

  const onImport = (responseData) => {
    const { import_task } = responseData;
    const categoryId = import_task.category_id;

    if (import_task) {
      queryClient.setQueriesData(['categories'], data => produce(data, draft => {
        // if a category that doesn't exist is fetched
        if (!data) {
          return;
        }

        const selectedCategory = Array.isArray(data) ? draft.find(category => category.id === categoryId) : (data.id === categoryId && draft);
        
        if (selectedCategory) {
          if (selectedCategory.unchecked_import_tasks) {
            selectedCategory.unchecked_import_tasks.unshift(import_task);
          }
          else {
            selectedCategory.unchecked_import_tasks = [import_task];
          }
        }
      }));
    }
  }

    const onMove = ({ id, lead, from_status, to_status }) => {
    console.log('variables:', id, from_status, to_status)

    queryClient.setQueriesData(['leads', 'status', to_status], data => produce(data, draft => {
      console.log('data.pages[0]:', data.pages[0])
      draft.pages[0].data.unshift({ ...lead, status_id: to_status });
    }));

    queryClient.setQueriesData(['leads', 'status', from_status], data => produce(data, draft => {
      console.log('data.pages[0]:', data.pages[0])

      for (let page of draft.pages) {
        const index = page.data.findIndex(l => l.id === lead.id);
        if (index !== -1) {
           page.data.splice(index, 1);
          break;
        }
      }
    }));
  }

  const onReorder = ({ statusId, lead, fromIndex, toIndex }) => {
    queryClient.setQueriesData(['leads', 'status', statusId], data => produce(data, draft => {
      for (let page of draft.pages) {
        console.log('page.data.length:', page.data.length)
        console.log('fromIndex:', fromIndex)
        console.log('toIndex:', toIndex)

        if (fromIndex !== -1 && fromIndex < page.data.length) {
          page.data.splice(fromIndex, 1);
          fromIndex = -1;
        }
        else {
          fromIndex -= page.data.length;
        }

        if (toIndex !== -1 && toIndex <= page.data.length) {
          page.data.splice(toIndex, 0, lead);
          toIndex = -1;
        }
        else {
          toIndex -= page.data.length;
        }
      }
    }))
  }

  return {
    updateLead             : useMutation({ mutationFn: updateLead, onSuccess: onUpdateSuccess }),
    updateLeadAsync        : useMutation({ mutationFn: updateLead, onMutate: onMutateLead }),
    undoDeleteMutation     : useMutation({ mutationFn: undoDeleteLead, onMutate: onUndoDelete }),
    moveLead               : useMutation({ mutationFn: () => {}, onMutate: onMove }),
    reorderLead            : useMutation({ mutationFn: () => {}, onMutate: onReorder }),
    insertMutation         : useMutation({ mutationFn: addLead, onSuccess: onAddLead }),
    deleteMutation         : useMutation({ mutationFn: deleteLead, onMutate: onDelete }),

    // Bulk Operations
    bulkDeleteMutation     : useMutation({ mutationFn: bulkDelete, onMutate: onBulkDelete }),
    bulkUndoDeleteMutation : useMutation({ mutationFn: bulkUndoDelete, onMutate: onBulkUndoDelete }),
    bulkEditMutation       : useMutation({ mutationFn: bulkEdit, onSuccess: onBulkEdit }),

    importMutation         : useMutation({ mutationFn: importLeads, onSuccess: onImport }),
  }
}
